import { FileTextIcon, LayoutGridIcon } from 'lucide-react';
import { useSnapshot } from 'valtio/react';

import { FlyoutType, openFlyout } from '@paid-ui/blocks/global-flyout';
import {
  DrawerClose,
  DrawerContent,
  DrawerOverlay,
  DrawerPortal,
  DrawerRoot,
  DrawerTrigger,
} from '@paid-ui/components/drawer';
import { Logo } from '@paid-ui/components/logo';
import { Separator } from '@paid-ui/components/separator';
import { UnstyledButton } from '@paid-ui/components/unstyled-button';
import { browserEnvs } from '@paid-ui/env';
import { AccountIcon } from '@paid-ui/icons/account';
import { ClaimsIcon } from '@paid-ui/icons/claims';
import { EmailIcon } from '@paid-ui/icons/email';
import { MenuIcon } from '@paid-ui/icons/menu';
import { ProjectsIcon } from '@paid-ui/icons/projects';
import { SettingsIcon } from '@paid-ui/icons/settings';
import { userManager } from '@paid-ui/models/user';
import { useSignOut } from '@paid-ui/mutations/sign-out';

import { NavbarLink } from '../navbar-link';
import { UserAvatar } from '../user-avatar';

const { env } = browserEnvs;
const isActiveDev = ['local', 'dev'].includes(env);

export const HamburgerMenu: React.FC = () => {
  const { displayName, hasBusiness, profile } = useSnapshot(userManager);
  const { mutate, isLoading: isPending } = useSignOut();
  const userName = [profile?.firstName, profile?.lastName].filter(Boolean).join(' ');

  const handleSignOut = () => {
    mutate();
  };

  return (
    <DrawerRoot>
      <DrawerTrigger className="inline-flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center bg-transparent text-white/60 no-underline transition-colors duration-150 ease-linear hover:text-white focus:text-white/80 sm:hidden">
        <MenuIcon className="h-6 w-6" />
      </DrawerTrigger>
      <DrawerPortal>
        <DrawerOverlay />
        <DrawerContent
          side="left"
          className="right-20 w-auto bg-black text-white landscape:right-[16.8%]"
        >
          <div className="flex h-full w-full flex-col overflow-y-auto overflow-x-hidden p-4">
            <header className="px-5 py-10">
              <Logo height={30} />
            </header>
            <nav className="flex flex-col gap-y-3.5">
              <div>
                <DrawerClose>
                  <NavbarLink href="/" isMobile>
                    <LayoutGridIcon className="h-8 w-8 shrink-0 p-1" />
                    <div>Dashboard</div>
                  </NavbarLink>
                </DrawerClose>
                {isActiveDev ? (
                  <DrawerClose>
                    <NavbarLink href="/projects" isMobile>
                      <ProjectsIcon className="h-8 w-8 shrink-0 p-1" />
                      <div>Projects</div>
                    </NavbarLink>
                  </DrawerClose>
                ) : null}
                <DrawerClose>
                  <NavbarLink href="/contracts" isMobile>
                    <FileTextIcon className="h-8 w-8 shrink-0 p-1" />
                    <div>Contracts</div>
                  </NavbarLink>
                </DrawerClose>
                <DrawerClose>
                  <NavbarLink href="/claims" isMobile>
                    <ClaimsIcon className="h-8 w-8 shrink-0 p-1" />
                    <div>Claims</div>
                  </NavbarLink>
                </DrawerClose>
                <DrawerClose>
                  <NavbarLink href="/account" isMobile>
                    <AccountIcon className="h-8 w-8 shrink-0 p-1" />
                    <span>Account</span>
                  </NavbarLink>
                </DrawerClose>
              </div>
              <Separator className="bg-grey-tertiary" />
              <div>
                <div className="mb-2.5 flex items-center gap-x-3 px-3">
                  <UserAvatar className="shrink-0" />
                  <div className="flex flex-1 flex-col">
                    <span className="text-base font-semibold empty:hidden">{userName}</span>
                    <span className="text-sm font-medium empty:hidden">
                      {hasBusiness ? displayName : 'Individual user'}
                    </span>
                  </div>
                </div>
                <DrawerClose>
                  <UnstyledButton
                    onClick={handleSignOut}
                    disabled={isPending}
                    className="ml-11 block cursor-pointer px-3 py-2.5 text-left text-base font-semibold text-white"
                  >
                    Sign out
                  </UnstyledButton>
                </DrawerClose>
              </div>
              <Separator className="bg-grey-tertiary" />
              <div>
                <UnstyledButton
                  className="flex items-center gap-x-3 px-3 text-white"
                  onClick={() => {
                    openFlyout(FlyoutType.HELP_CENTER, {
                      className: 'w-[752px]',
                      showCloseIcon: true,
                      closeOnClickOutside: true,
                    });
                  }}
                >
                  <EmailIcon className="h-8 w-8 shrink-0 p-1 text-white" />
                  <span className="flex-1 text-base font-semibold">Help</span>
                </UnstyledButton>
                <DrawerClose>
                  <NavbarLink href="/settings" isMobile>
                    <SettingsIcon className="h-8 w-8 shrink-0 p-1" />
                    <div>Settings</div>
                  </NavbarLink>
                </DrawerClose>
              </div>
            </nav>
          </div>
        </DrawerContent>
      </DrawerPortal>
    </DrawerRoot>
  );
};
